import React, { FC } from 'react';

import {
  TextField as MuiTextField,
  styled,
  TextFieldProps,
} from '@mui/material';

import { getCountText } from '~/components/atoms/inputs/helpers/functions';
import { colors } from '~/theme/colors';

export type StyledTextFieldProps = TextFieldProps;

export type TextFieldCustomProps = TextFieldProps & {
  maxCountIndicator?: number;
  minCountWarning?: number;
  fullHeight?: string;
  hideHelperText?: boolean;
  customStyles?: Record<string, any>;
};

export const StyledTextField = styled(MuiTextField, {
  name: 'StyledTextField',
})<TextFieldCustomProps>(({ fullHeight, hideHelperText, customStyles }) => ({
  '& .MuiInputBase-root': {
    borderRadius: '8px',

    ...(fullHeight && {
      height: '100%',
      display: 'flex',
      alignItems: 'stretch',
    }),

    '&.MuiInputBase-colorWarning.Mui-focused + .MuiFormHelperText-root': {
      color: colors.warning_50,
    },

    '& .MuiList-root': {
      backgroundColor: colors.white,
    },
  },

  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    border: '2px solid var(--color-custom-primary_50)',
  },

  '& .MuiInputLabel-root': {
    color: colors.black,
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    marginBottom: '8px',
    position: 'static',
    transform: 'unset',

    '.MuiFormLabel-asterisk': {
      display: 'none',
    },

    '&.Mui-error': {
      color: 'var(--color-custom-error_40)',
    },
  },

  '& .MuiInputBase-input': {
    padding: '10px 12px',
    fontSize: '14px',
    lineHeight: '20px',

    ...(fullHeight && {
      height: '100%',
      boxSizing: 'border-box',
    }),

    '&::placeholder': {
      color: '#8C8C90',
      opacity: 1,
    },
  },

  '& .MuiInputBase-inputSizeLarge': {
    padding: '14px 16px',
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.25px',
  },

  '& .MuiInputBase-multiline': {
    padding: '14px 16px',

    ...(fullHeight && {
      height: '100%',
      display: 'flex',
      alignItems: 'stretch',
    }),

    '& .MuiInputBase-input': {
      padding: '0px',
      fontSize: '14px',
      lineHeight: '20px',
    },

    '& textarea': fullHeight
      ? {
          height: '100% !important',
          overflowY: 'auto !important',
          resize: 'none',
          boxSizing: 'border-box',
        }
      : {},
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid var(--color-custom-neutral_60)',
    top: 0,

    '&.Mui-error': {
      borderWidth: '2px solid var(--color-custom-error_40)',
    },
  },

  '& .MuiInputAdornment-root': {
    margin: 0,
  },

  '& .MuiInputBase-adornedStart': {
    padding: 0,
  },

  '& .MuiFormHelperText-root': {
    ...(hideHelperText
      ? { display: 'none' }
      : {
          fontSize: '12px',
          lineHeight: '20px',
          margin: '8px 12px 0',
        }),
  },

  '& legend': {
    display: 'none',
  },

  ...(customStyles ?? {}),
}));

const TextField: FC<TextFieldCustomProps> = ({
  maxCountIndicator,
  minCountWarning,
  color,
  value,
  ...props
}) => {
  const { countText, minCountText } = getCountText(
    value as string,
    minCountWarning,
    maxCountIndicator,
  );

  const inputColor = minCountText ? 'warning' : null;

  return (
    <StyledTextField
      value={value}
      helperText={minCountText || countText}
      {...(inputColor ? { color: color || inputColor } : {})}
      {...props}
    />
  );
};

export default TextField;
